import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { JurisdictionBadge } from "./JurisdictionBadge";
import { Button } from "@/components/ui/button";
import { MessageSquarePlus, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import type { OnboardingDataPoint } from "@/data/onboardingData";
import { useState } from "react";

interface DataPointListProps {
  dataPoints: OnboardingDataPoint[];
  selectedKeys: Set<string>;
  onToggle: (key: string) => void;
  onRequestMissing: () => void;
  readOnly?: boolean;
  subsections?: { label: string; keys: string[] }[];
}

export function DataPointList({
  dataPoints,
  selectedKeys,
  onToggle,
  onRequestMissing,
  readOnly = false,
  subsections,
}: DataPointListProps) {
  const typical = dataPoints.filter(dp => dp.typical);
  const additional = dataPoints.filter(dp => !dp.typical);

  const subsectionMap = new Map<string, OnboardingDataPoint[]>();
  const ungroupedAdditional: OnboardingDataPoint[] = [];

  if (subsections) {
    const keyToSubsection = new Map<string, string>();
    subsections.forEach(s => s.keys.forEach(k => keyToSubsection.set(k, s.label)));
    additional.forEach(dp => {
      const section = keyToSubsection.get(dp.key);
      if (section) {
        const arr = subsectionMap.get(section) || [];
        arr.push(dp);
        subsectionMap.set(section, arr);
      } else {
        ungroupedAdditional.push(dp);
      }
    });
  }

  return (
    <ScrollArea className="h-full">
      <div className="space-y-6 p-4">
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Typical Data Points
          </h4>
          <div className="space-y-2">
            {typical.map(dp => (
              <DataPointRow key={dp.key} dp={dp} checked={selectedKeys.has(dp.key)} onToggle={onToggle} readOnly={readOnly} />
            ))}
          </div>
        </div>

        {additional.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Additional Data Points
            </h4>
            {subsections && subsections.length > 0 ? (
              <div className="space-y-2">
                {subsections.map(section => {
                  const items = subsectionMap.get(section.label) || [];
                  if (items.length === 0) return null;
                  return <SubsectionGroup key={section.label} label={section.label} items={items} selectedKeys={selectedKeys} onToggle={onToggle} readOnly={readOnly} />;
                })}
                {ungroupedAdditional.map(dp => (
                  <DataPointRow key={dp.key} dp={dp} checked={selectedKeys.has(dp.key)} onToggle={onToggle} readOnly={readOnly} />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {additional.map(dp => (
                  <DataPointRow key={dp.key} dp={dp} checked={selectedKeys.has(dp.key)} onToggle={onToggle} readOnly={readOnly} />
                ))}
              </div>
            )}
          </div>
        )}

        <div className="border-t pt-4">
          <p className="flex items-center gap-2 px-3 text-sm text-muted-foreground">
            <MessageSquarePlus className="h-4 w-4" />
            Track more than this? We cover dozens of data points per jurisdiction.
          </p>
        </div>
      </div>
    </ScrollArea>
  );
}

function DataPointRow({ dp, checked, onToggle, readOnly }: { dp: OnboardingDataPoint; checked: boolean; onToggle: (key: string) => void; readOnly: boolean }) {
  return (
    <label className={`flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer transition-colors ${checked ? "border-primary/40 bg-primary/5" : "border-border hover:bg-muted/50"}`}>
      <Checkbox checked={checked} onCheckedChange={() => !readOnly && onToggle(dp.key)} disabled={readOnly} />
      <span className="flex-1 text-sm">{dp.label}</span>
      <JurisdictionBadge level={dp.jurisdiction} showLabel />
    </label>
  );
}

function SubsectionGroup({ label, items, selectedKeys, onToggle, readOnly }: { label: string; items: OnboardingDataPoint[]; selectedKeys: Set<string>; onToggle: (key: string) => void; readOnly: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center gap-2 w-full text-sm font-medium py-1 hover:text-primary transition-colors">
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "" : "-rotate-90"}`} />
        {label}
        <span className="text-xs text-muted-foreground">({items.length})</span>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2 pl-6 pt-1">
        {items.map(dp => (
          <DataPointRow key={dp.key} dp={dp} checked={selectedKeys.has(dp.key)} onToggle={onToggle} readOnly={readOnly} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}
